import { Card, CardContent, CardHeader, PaperTypeMap, SxProps, Theme, Typography, styled } from "@mui/material";
import { DateTime } from "luxon";
import React from "react";

import { CustomerNoteType } from "../../../Models/CustomerNote";
import ICustomerNote from "../Types/PreviousCustomerNote";

export const classNames = {
  header: "PreviousCustomerNotHeader",
  content: "PreviousCustomerNoteContent"
};

interface NoteTypeProps {
  headerText: string;
  className: string;
}

export const noteTypeNameMap: Record<CustomerNoteType, NoteTypeProps> = {
  [CustomerNoteType.Generic]: { headerText: "", className: "previousCustomerNoteGeneric" },
  [CustomerNoteType.ShowDuringCheckIn]: {
    headerText: "Show During Check-In",
    className: "previousCustomerNoteShowDuringCheckIn"
  },
  [CustomerNoteType.ShowOnHistory]: {
    headerText: "Show on Guest History",
    className: "previousCustomerNoteShowOnHistory"
  },
  [CustomerNoteType.ShowWhenBooking]: {
    headerText: "Show When Booking",
    className: "previousCustomerNoteShowWhenBooking"
  },
  [CustomerNoteType.ShowWhenPayment]: {
    headerText: "Show When Payment",
    className: "previousCustomerNoteShowWhenPayment"
  }
};

/** A rendering of a previously created customer note. Styling is applied based on the note type. */
const PreviousCustomerNote: React.FC<Pick<ICustomerNote, "text" | "type" | "createdAt">> = ({
  text,
  type,
  createdAt
}) => {
  const noteSplitByNewlines: string[] = text.split("\n");

  return (
    <NoteCard className={noteTypeNameMap[type].className} type={type}>
      <CardHeader
        className={classNames.header}
        title={`${createdAt.toLocaleString(DateTime.DATE_MED)}: ${noteTypeNameMap[type].headerText}`}
        titleTypographyProps={{ variant: "h6" }}
      />
      <CardContent className={classNames.content}>
        {noteSplitByNewlines.map((noteLine, key) => (
          <Typography variant="body2" key={key} style={{ wordBreak: "break-word", hyphens: "auto" }}>
            {noteLine}
          </Typography>
        ))}
      </CardContent>
    </NoteCard>
  );
};

interface NotePaperProps extends Partial<PaperTypeMap> {
  type: CustomerNoteType;
}

const NoteCard = styled(Card, {
  shouldForwardProp: prop => prop !== "type"
})<NotePaperProps>(({ type, theme }) => {
  let style: SxProps<Theme>;
  switch (type) {
    case CustomerNoteType.Generic:
      style = {
        "& .MuiCardHeader-root": {
          backgroundColor: theme.palette.action.focus,
          color: theme.palette.text.primary
        }
      };
      break;
    case CustomerNoteType.ShowOnHistory:
      style = {
        "& .MuiCardHeader-root": {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText
        }
      };
      break;
    case CustomerNoteType.ShowDuringCheckIn:
      style = {
        "& .MuiCardHeader-root": {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText
        }
      };
      break;
    case CustomerNoteType.ShowWhenBooking:
      style = {
        "& .MuiCardHeader-root": {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText
        }
      };
      break;
    case CustomerNoteType.ShowWhenPayment:
      style = {
        "& .MuiCardHeader-root": {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText
        }
      };
      break;
    default:
      throw `Failed to define the note style for ${type}.`;
  }
  return {
    "&": { ...style },
    "& .MuiCardHeader-root": { padding: `${theme.spacing(0.2)} ${theme.spacing(1.2)}` },
    "& .MuiCardContent-root": { padding: `${theme.spacing(0.5)} ${theme.spacing(0.8)}` }
  };
});

export default PreviousCustomerNote;
